<template>
    <div
        :class="`formulate-input-element formulate-input-element--${context.class}`"
        :data-type="context.type"
    >
        <MultiSelect 
            v-model="selectedTuObjects" 
            :options="finalOptions" 
            optionLabel="full_name"
            :optionDisabled="disabledField"
            dataKey="id" 
            :filter="true" 
            :placeholder="placeholder" 
            display="chip"
            :selectionLimit="selectionLimit"
            class="w-100"
            v-bind="context.attributes"
            @input="handleSelection"
            @before-show="onBeforeShow"
            @hide="context.blurHandler"
        >   <!-- @input="handleSelection" optionValue="id" - up/down arrows did not work when using optionValue -- it screws up what is shown in value slot I think -->
            <template #indicator>
                <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>  <!-- had to add this slot, even though it has default -->
            </template>
            <template #value="slotProps">
                <div v-if="loadingOptions" key="loadingOptions" class="text-muted">
                    <b-spinner small class="text-muted mr-1"></b-spinner>
                    <!-- <i class="pi pi-spin pi-spinner" /> -->
                    Loading...
                </div>
                <div v-else-if="slotProps.value && slotProps.value.length > 0" class="d-flex flex-wrap gap-2" key="hasValue">
                    <tenant-user-badge
                        v-for="tu in slotProps.value"
                        :key="tu.id"
                        :tu-data="tu"
                    ></tenant-user-badge>
                </div>
            </template>
            <template #option="slotProps">
                <div v-if="loadingOptions" key="loadingOptions">
                    <b-spinner small class="text-muted mr-1"></b-spinner>
                    Loading...
                </div>
                <div v-else key="optionsLoaded" class="">
                    {{ slotProps.option.full_name ? slotProps.option.full_name : 'Loading...' }}
                    <b-badge :variant="'soft-' + (slotProps.option.is_internal ? 'primary' : 'danger')" class="text-uppercase ml-2 small">{{ slotProps.option.is_internal ? 'Internal' : 'External'}}</b-badge>
                </div>
            </template>
        </MultiSelect>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import MultiSelect from 'primevue/multiselect';

// formulate custom inputs:  must read from and write to context.model
// for this one, we'll assume that is only the ID of the company/vendor
// then we need to get options as needed, from here
// no options passed in from parent?  or have a prop set to fetch companies vs. get from prop?

export default {
    name: 'FormulateTenantUserPicker',
    components: {
        MultiSelect,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        context: {  // the default/required prop for Vue-Formulate field components
            type: Object,
            required: true,
        },
        selectionLimit: {  // OPTIONAL max number of selections - passed on to Multiselect component
            type: Number,
            required: false,
        },
        filters: {  // filters to use in getOptions as query_params - backend filterset_fields must be able to handle them, or they're ignored
            type: Object,
            required: false,
        },
        disabledField: {
            type: String,
            default: 'unassignable',
        },
    },
    data() {
        return {
            loadingOptions: false,
            selectedTuIds: [],  // set any pre-selections here -- we'll set selectedTuObjects when we getOptions
            selectedTuObjects: [],
            fetchedOptions: null,  // we fetch when user clicks on dropdown - use null until we know we've fetched them
            didFetchOptions: false,
        }
    },
    computed: {
        finalOptions() {
            // warning... if you set 'classification' to something that does not have options prop, then context.options = false
            return this.fetchedOptions ?? [{id: 'LOADING'}];  // should be array of option objects - using a dummy one as placeholder when loading
        },
        readOnly() {
            // this is for later... for showing in complete read-only mode
            return this.context.attributes?.disabled ?? false;
        },
        placeholder() {
            return this.context.attributes?.placeholder ?? 'Select team members...';
        }
    },
    mounted() {
        // do we already have a selected value?  if not an object, just the ID, then we need to get options now
        // TODO - if in READ ONLY mode, then you may not want to get the whole list 
        //      (because you might have vendors looking at in, like in a workflow)
        //      in that case, you may want to just do a retrieve() instead of list() from API
        const currentPropValue = this.context.model;
        if (currentPropValue && Array.isArray(currentPropValue) && currentPropValue.length) {
            this.selectedTuIds = [...currentPropValue];
            console.log('mounted tu picker');
            console.log('currentPropValue is ', currentPropValue);
            this.getOptions();
        }
    },
    methods: {
        async onBeforeShow() {
            // if we havne't fetched options yet, then do it now
            if (!this.didFetchOptions) {
                await this.getOptions();
            }
        },
        async getOptions() {
            this.loadingOptions = true;
            let axiosConfig = {};
            if (this.filters) {
                axiosConfig['params'] = {...this.filters};
            }
            try {
                // NOTE - .list() gets ALL internal TUs, even ones with removed portal access, AND EVEN DEACTIVATED ONES.  list_portal_users() gets all currently with portal access
                // you can use this for displaying read-only things, and for those you should get ALL TUs, but set disabled=True if they are not active
                // we added 'unassignable' to all TU serializers, which means they may exist, but they are missing email or something -- use that as disabledField prop
                //const { data } = await suppliosApi.internalTu.list_portal_users(axiosConfig);  // not available to external people!  I think...
                // Jan 22 2023 - changed to list_assignable_users -- so we can use lazy invites for people who can have access, but do not yet
                const { data } = await suppliosApi.internalTu.list_assignable_users(axiosConfig);
                //list_portal_users was paginated, but list_assignable_users is NOT (because we really only use it in front-end dropdowns)....
                const results = (!!data && Array.isArray(data)) ? data : data.results;
                this.fetchedOptions = results ? [ ...results ] : [];  // - default to empty list so we know we've fetched once
                console.log('done fetching tu picker');
                if (this.selectedTuIds && Array.isArray(this.selectedTuIds) && this.selectedTuIds.length) {
                    this.selectedTuObjects = this.fetchedOptions.filter(tu => this.selectedTuIds.includes(tu.id));
                }
                this.didFetchOptions = true;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingOptions = false;
        },
        handleSelection(selectedObjects) {
            console.log('selectedObjects', selectedObjects);
            // if we have optionValue="id" then this will be the ID string -- otherwise will be the full object
            //let selectedIds = [];  
            if (selectedObjects && Array.isArray(selectedObjects) && selectedObjects.length) {
                //this.selectedTuObjects = selectedObjects;
                const selectedIds = selectedObjects.map(sel => sel.id); //[...selectedIds];
                this.selectedTuIds = selectedIds;
                this.context.model = selectedIds;
            } else {
                //this.selectedTuObjects = [];
                this.selectedTuIds = [];
                this.context.model = [];
            }
            
        }
    }
}
</script>

<style>

</style>